import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Button, message, Divider, Space, Spin, Alert } from 'antd';
import { SettingOutlined, SaveOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext.jsx';
import apiService from '../services/apiService';

const ThresholdSettingsModal = ({ 
  visible, 
  onClose, 
  project, 
  onThresholdUpdate 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentThresholds, setCurrentThresholds] = useState(null);
  const [thresholdSource, setThresholdSource] = useState(null);
  const [userThresholds, setUserThresholds] = useState(null);
  const { getAuthToken } = useAuth();

  useEffect(() => {
    if (visible && project) {
      loadProjectThresholds();
      loadUserThresholds();
    }
  }, [visible, project]);

  const loadProjectThresholds = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await apiService.getProjectThresholds(project.id, token);
      
      setCurrentThresholds(response.data);
      setThresholdSource(response.source);
      
      // Set form values
      form.setFieldsValue({
        scorecard_1_min: response.data.scorecard_1?.min_score,
        scorecard_1_max: response.data.scorecard_1?.max_score,
        scorecard_2_min: response.data.scorecard_2?.min_score,
        scorecard_2_max: response.data.scorecard_2?.max_score,
        scorecard_3_min: response.data.scorecard_3?.min_score,
        scorecard_3_max: response.data.scorecard_3?.max_score,
        scorecard_4_min: response.data.scorecard_4?.min_score,
        scorecard_4_max: response.data.scorecard_4?.max_score,
      });
    } catch (error) {
      console.error('Failed to load project thresholds:', error);
      message.error('Failed to load threshold settings');
    } finally {
      setLoading(false);
    }
  };

  const loadUserThresholds = async () => {
    try {
      const token = getAuthToken();
      const response = await apiService.getThresholdSettings(token);
      if (!response.is_default) {
        setUserThresholds(response.data);
      }
    } catch (error) {
      console.log('User has no global thresholds');
    }
  };

  const handleSave = async (values) => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      const thresholdData = {
        scorecard_1: {
          min_score: values.scorecard_1_min,
          max_score: values.scorecard_1_max
        },
        scorecard_2: {
          min_score: values.scorecard_2_min,
          max_score: values.scorecard_2_max
        },
        scorecard_3: {
          min_score: values.scorecard_3_min,
          max_score: values.scorecard_3_max
        },
        scorecard_4: {
          min_score: values.scorecard_4_min,
          max_score: values.scorecard_4_max
        }
      };

      await apiService.updateProjectThresholds(project.id, thresholdData, token);
      
      message.success('Threshold settings updated successfully!');
      
      if (onThresholdUpdate) {
        onThresholdUpdate();
      }
      
      // Reload to get updated source
      await loadProjectThresholds();
      
    } catch (error) {
      console.error('Failed to update thresholds:', error);
      message.error('Failed to update threshold settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyFromUser = async () => {
    if (!userThresholds) {
      message.warning('You have no global threshold settings to copy');
      return;
    }

    try {
      setLoading(true);
      const token = getAuthToken();
      
      const thresholdData = {
        scorecard_1: userThresholds.scorecard_1,
        scorecard_2: userThresholds.scorecard_2,
        scorecard_3: userThresholds.scorecard_3,
        scorecard_4: userThresholds.scorecard_4
      };

      await apiService.updateProjectThresholds(project.id, thresholdData, token);
      
      message.success('Global threshold settings copied to project!');
      
      if (onThresholdUpdate) {
        onThresholdUpdate();
      }
      
      await loadProjectThresholds();
      
    } catch (error) {
      console.error('Failed to copy user thresholds:', error);
      message.error('Failed to copy threshold settings');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefaults = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      // Delete project-specific thresholds to fall back to user/defaults
      await apiService.deleteProjectThresholds(project.id, token);

      message.success('Reset to default threshold settings!');
      
      if (onThresholdUpdate) {
        onThresholdUpdate();
      }
      
      await loadProjectThresholds();
      
    } catch (error) {
      console.error('Failed to reset thresholds:', error);
      message.error('Failed to reset threshold settings');
    } finally {
      setLoading(false);
    }
  };

  const getSourceDescription = () => {
    switch (thresholdSource) {
      case 'project':
        return { text: 'Project-specific settings', color: '#8b5cf6' };
      case 'user':
        return { text: 'Using your global settings', color: '#10b981' };
      case 'default':
        return { text: 'Using system defaults', color: '#6b7280' };
      default:
        return { text: 'Unknown source', color: '#6b7280' };
    }
  };

  const sourceInfo = getSourceDescription();

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined style={{ color: '#8b5cf6' }} />
          <span>Threshold Settings - {project?.name}</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={null}
    >
      <Spin spinning={loading}>
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={
              <div>
                <strong>Current Status:</strong> 
                <span style={{ color: sourceInfo.color, marginLeft: 8 }}>
                  {sourceInfo.text}
                </span>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <p style={{ 
            color: 'var(--text-secondary)', 
            fontSize: '13px',
            lineHeight: '1.4',
            margin: 0
          }}>
            Set threshold ranges (1-10 scale) for each scorecard. Apps with scores outside 
            these ranges will be flagged for review in this project.
          </p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          {[1, 2, 3, 4].map(scorecard => (
            <div key={scorecard} style={{ 
              marginBottom: 16, 
              padding: 12,
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-secondary)'
            }}>
              <div style={{ 
                marginBottom: 8, 
                fontWeight: 500,
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}>
                Scorecard {scorecard}
              </div>
              
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <Form.Item
                    name={`scorecard_${scorecard}_min`}
                    label="Min Score"
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'number', min: 1, max: 10, message: 'Must be 1-10' }
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      min={1}
                      max={10}
                      style={{ width: '100%' }}
                      placeholder="Min"
                    />
                  </Form.Item>
                </div>
                
                <div style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: '14px',
                  paddingTop: '24px'
                }}>
                  to
                </div>
                
                <div style={{ flex: 1 }}>
                  <Form.Item
                    name={`scorecard_${scorecard}_max`}
                    label="Max Score"
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'number', min: 1, max: 10, message: 'Must be 1-10' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const minValue = getFieldValue(`scorecard_${scorecard}_min`);
                          if (!value || !minValue || value >= minValue) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Max must be ≥ Min'));
                        },
                      })
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      min={1}
                      max={10}
                      style={{ width: '100%' }}
                      placeholder="Max"
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          ))}

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              {userThresholds && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleCopyFromUser}
                  disabled={loading}
                >
                  Copy My Global Settings
                </Button>
              )}
              
              {thresholdSource === 'project' && (
                <Button
                  icon={<DeleteOutlined />}
                  onClick={handleResetToDefaults}
                  disabled={loading}
                  danger
                >
                  Reset to Defaults
                </Button>
              )}
            </Space>

            <Space>
              <Button onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={loading}
              >
                Save Changes
              </Button>
            </Space>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ThresholdSettingsModal;